'use client';

import { AdaptiveHeight } from '@florencelabs/adaptive-height';
import { Button } from '@florencelabs/button';
import { Fade } from '@florencelabs/fade';
import { MaterialSymbol } from '@florencelabs/icons';
import { ModalCloseButton, Modal, Dialog } from '@florencelabs/modal-2';
import { isTooltipOpen } from '@florencelabs/tooltip';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useState, ReactElement, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getDecisionTreeScenes,
  DecisionTreeScene,
} from '@provider-directory/app/ui/sceneFlows';
import { useAppointment } from '@provider-directory/contexts/AppointmentContext';
import {
  useDecisionTree,
  getSchedulingFlowType,
} from '@provider-directory/contexts/DecisionTreeContext';
import {
  ProviderContext,
  ProviderContextProvider,
} from '@provider-directory/contexts/ProviderContext';
import {
  DECISION_TREE_INITIAL_STEP,
  DecisionTreeSceneSlug,
  getDecisionTreeLocation,
  getNavigationOptions,
  SceneSlug,
} from '@provider-directory/models/decision-tree.model';
import type { ProviderDetailsResource } from '@provider-directory/types/providers.contracts';
import { useAnalytics } from '../analytics/analytics-provider';
import { ProgressBar } from '../progress-bar/progress-bar.component';
import DecisionTreeSkeleton from './decision-tree-skeleton';
import { GenericDecisionTreeError } from './errors';

const DecisionTreeHeading = ({
  Scene,
  isLoadingNextScene,
}: {
  Scene: DecisionTreeScene;
  isLoadingNextScene: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={clsx(
        'absolute top-0 h-[88px]',
        'flex w-full',
        'flex-col gap-6 lgTablet:flex-row',
        'z-40 bg-gradient-to-b from-primary-100 from-50% lgTablet:rounded-t-lg',
      )}
    >
      <div
        className={clsx(
          'grow-0 lgTablet:grow lgTablet:self-center',
          'pl-0 lgTablet:pl-16 desktop:pl-12',
        )}
        aria-hidden={true}
      >
        <ProgressBar
          percent={isLoadingNextScene ? 100 : Scene.props.progress}
        />
      </div>
      <div
        className={clsx(
          'grow self-end lgTablet:grow-0 lgTablet:self-center',
          'pr-6 tablet:pr-16 desktop:pr-12',
        )}
      >
        <ModalCloseButton
          className={'static'}
          ariaLabel={t('decisionTree.close')}
        />
      </div>
    </div>
  );
};

const DecisionTreeNavigation = ({
  Scene,
  onBack,
  onNext,
  isLoadingNextScene,
  canNavigateBack,
}: {
  Scene: DecisionTreeScene;
  onBack: () => void;
  onNext: (
    slug: SceneSlug,
    options?: {
      redirectLink?: string;
    },
  ) => void;
  isLoadingNextScene: boolean;
  canNavigateBack: boolean;
}) => {
  const { t } = useTranslation();

  const nextButtonText =
    Scene.type === 'decisionTreeDisclaimer' && Scene.props.nextButtonText
      ? Scene.props.nextButtonText
      : 'next';

  const handleNextButtonClick = () => {
    if (Scene.type === 'decisionTreeDisclaimer') {
      onNext(
        Scene.props.nextScene,
        getNavigationOptions(Scene.props.nextScene),
      );
    }
  };

  return (
    <div
      className={clsx(
        'border-t border-gray-200 bg-white py-4 lgTablet:rounded-b-lg desktop:py-6',
      )}
    >
      <div
        className={'mx-auto flex items-center px-6 tablet:px-16 desktop:px-10'}
      >
        {canNavigateBack ? (
          <Button
            testId={'modal-back-btn'}
            variant={'systemLink'}
            className={clsx(
              'flex cursor-pointer flex-row gap-x-2 !p-0 align-middle text-gray-950',
              'duration-500 animate-in fade-in',
            )}
            onPress={onBack}
          >
            <MaterialSymbol name={'chevron_left'} />
            <span className={'align-middle text-button-medium'}>
              {t('back')}
            </span>
          </Button>
        ) : null}
        <Button
          testId={'modal-next-btn'}
          variant={'clientMain'}
          className={'ml-auto'}
          type={'submit'}
          size={'small'}
          form={Scene.props.name}
          isLoading={isLoadingNextScene}
          onClick={handleNextButtonClick}
        >
          {t(nextButtonText)}
        </Button>
      </div>
    </div>
  );
};

export const DecisionTreeModal = (): ReactElement | null => {
  const router = useRouter();
  const {
    specialtyBranch,
    currentScene,
    canNavigateBack,
    navigateToScene,
    navigateBack,
    isOpen,
    setIsOpen: setOpen,
    isRestoringMemory,
    isLoading,
    setIsLoading,
  } = useDecisionTree();
  const { appointment } = useAppointment();
  const { t } = useTranslation();

  const analytics = useAnalytics();
  const modalContentRef = useRef<HTMLDivElement | null>(null);
  // Track previous scene to determine if we should auto-focus heading (for step navigation a11y)
  const previousSceneRef = useRef<string | null>(null);
  const wasOpenRef = useRef(false);

  const [hasStartedRecording, setHasStartedRecording] = useState(false);

  // Determine if we should auto-focus the heading:
  // - Only when navigating between scenes (not on initial modal open)
  // - wasOpenRef tracks if modal was open on previous render
  // - previousSceneRef tracks the scene from previous render
  const shouldAutoFocusHeading =
    wasOpenRef.current &&
    previousSceneRef.current !== null &&
    previousSceneRef.current !== currentScene;

  // Update refs in useEffect (after commit) to avoid issues with React 18 strict mode
  // where refs mutated during render get stale on the second render pass
  useEffect(() => {
    wasOpenRef.current = isOpen;
    previousSceneRef.current = isOpen ? currentScene : null;
  }, [isOpen, currentScene]);

  const decisionTreeScenes = useMemo(() => getDecisionTreeScenes(), []);

  const Scene = specialtyBranch
    ? decisionTreeScenes[specialtyBranch][currentScene]
    : null;
  const isMissingData = !appointment.provider || !Scene;

  const isErrorScene = Scene?.type === 'decisionTreeError';

  const onNext = (
    nextSceneId: DecisionTreeSceneSlug,
    options?: { redirectLink?: string },
  ): void => {
    if (options?.redirectLink) {
      setIsLoading(true);
      router.push(options.redirectLink);
    } else {
      navigateToScene(nextSceneId);
    }
  };

  const onBack = (): void => {
    const didNavigateBack = navigateBack();
    if (!didNavigateBack) {
      setOpen(false);
    }
  };

  const modalContent = isMissingData ? (
    <GenericDecisionTreeError />
  ) : (
    <>
      {!isErrorScene && (
        <DecisionTreeHeading Scene={Scene} isLoadingNextScene={isLoading} />
      )}
      <div
        ref={modalContentRef}
        className={clsx(
          'adaptive-height max-h-[calc(100dvh-72px)] overflow-y-auto lgTablet:max-h-[calc(100dvh-72px-48px*2)]',
        )}
      >
        <AdaptiveHeight aria-live={'off'}>
          <Fade fadeOn={Scene.sceneSlug}>
            {isLoading ? (
              <DecisionTreeSkeleton />
            ) : (
              <Scene.component
                key={Scene.sceneSlug}
                {...Scene.props}
                onNext={onNext}
                autoFocusHeading={shouldAutoFocusHeading}
              />
            )}
          </Fade>
        </AdaptiveHeight>
      </div>
      <div className={'mt-auto lgTablet:mt-0'}>
        <AdaptiveHeight aria-live={'off'}>
          <Fade fadeOn={isErrorScene ? 'hide' : 'show'}>
            {!isErrorScene ? (
              <DecisionTreeNavigation
                onBack={onBack}
                onNext={(scene, options) => {
                  onNext(scene as DecisionTreeSceneSlug, options);
                }}
                Scene={Scene}
                isLoadingNextScene={isLoading}
                canNavigateBack={canNavigateBack}
              />
            ) : null}
          </Fade>
        </AdaptiveHeight>
      </div>
    </>
  );

  // Send analytics when modal opens or scene changes, but not during memory restoration
  useEffect(() => {
    if (!isOpen || isRestoringMemory || !appointment.provider) return;

    const location = getDecisionTreeLocation(
      appointment.provider,
      appointment.departmentId,
    );
    if (!location) return;

    analytics.dTreeQuestionShownEvent({
      location,
      specialtyBranch,
      questionId: currentScene,
      provider: appointment.provider,
      schedulingFlowType: getSchedulingFlowType(),
    });
  }, [
    isOpen,
    isRestoringMemory,
    analytics,
    specialtyBranch,
    currentScene,
    appointment.provider,
    appointment.departmentId,
  ]);

  // Start session recording when modal is opened
  useEffect(() => {
    if (!window.mixpanel) return;
    if (
      !isOpen ||
      hasStartedRecording ||
      currentScene !== DECISION_TREE_INITIAL_STEP
    )
      return;

    try {
      window.mixpanel.start_session_recording();
      setHasStartedRecording(true);
    } catch (error) {
      console.warn('Failed to start session recording:', error);
    }
  }, [isOpen, currentScene, hasStartedRecording]);

  // Stop session recording when modal is closed
  useEffect(() => {
    if (!window.mixpanel || !hasStartedRecording || isOpen) return;

    try {
      window.mixpanel.stop_session_recording();
      setHasStartedRecording(false);
    } catch (error) {
      console.warn('Failed to stop session recording:', error);
    }
  }, [isOpen, hasStartedRecording]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (modalContentRef.current) {
        modalContentRef.current.scrollTo({ top: 0 });
      }
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [Scene?.sceneSlug]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={setOpen}
      isDismissable={true}
      shouldCloseOnInteractOutside={() => {
        return !isTooltipOpen();
      }}
    >
      <Dialog
        aria-label={t('decisionTree.close')}
        className={
          'h-dvh w-screen overflow-hidden bg-primary-100 text-left lgTablet:h-auto lgTablet:w-[598px] lgTablet:rounded-lg'
        }
      >
        {modalContent}
      </Dialog>
    </Modal>
  );
};

export interface DecisionTreeModalWrapperProps {
  /** When set (e.g. in Storybook), bypasses provider fetch so location/slots load immediately. */
  initialProvider?: ProviderDetailsResource | null;
  children?: React.ReactNode;
}

export default function DecisionTreeModalWrapper({
  initialProvider,
  children,
}: DecisionTreeModalWrapperProps = {}): ReactElement {
  const { appointment } = useAppointment();
  const modal = children ?? <DecisionTreeModal />;

  if (initialProvider !== undefined) {
    return (
      <ProviderContext.Provider
        value={{ provider: initialProvider, isLoading: false }}
      >
        {modal}
      </ProviderContext.Provider>
    );
  }

  return (
    <ProviderContextProvider npi={appointment.provider?.npi ?? null}>
      {modal}
    </ProviderContextProvider>
  );
}
